/**
 * MarketSnapshotWorkflowConfigModal Component
 *
 * Modal for configuring default market snapshot instrument/timeframe pairs.
 * These defaults drive auto-population of snapshots in the Pre-Trading workflow.
 *
 * Key behaviors:
 * - Manual Save/Discard pattern with dirty tracking
 * - SortableList for drag-and-drop reordering (order matters for auto-population)
 * - InstrumentDropdown for selecting instruments (grouped by asset class, supports inactive reactivation)
 * - TimeframeDropdown for selecting timeframes
 * - Validation: required fields, unique instrumentId+timeframe combinations
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Card,
  CardBody,
  Link,
  SortableList,
  DragHandle,
  EmptyState,
  Alert,
  useToastNotification,
} from '@enfomentis/ui';
import type { DragHandleProps } from '@enfomentis/ui';
import { TimeframeDropdown } from '@/components/shared/TimeframeDropdown';
import { InstrumentDropdown } from '@/components/shared/InstrumentExplorer';
import { marketSnapshotSettingsService } from '@/services/MarketSnapshotSettingsService';
import { instrumentService } from '@/services/InstrumentService';
import type { Instrument } from '@/types/schema/Instrument';
import { generateUUID } from '@/utils';

// ============================================================================
// Types
// ============================================================================

/**
 * Local entry with UUID for SortableList tracking
 */
interface LocalSnapshotEntry {
  id: string; // UUID for SortableList key (local only, stripped on save)
  instrumentId: string; // Real instrument ID from user's library
  timeframe: string; // canonical value like "15m", "4H"
}

/**
 * Validation errors for a single entry
 */
interface EntryValidationErrors {
  instrumentId?: string;
  timeframe?: string;
  duplicate?: string; // Row-level duplicate error
}

export interface MarketSnapshotWorkflowConfigModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Callback on successful save */
  onSaveSuccess: () => void;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Convert persisted entries to local entries with UUIDs
 */
function toLocalEntries(
  entries: Array<{ instrumentId: string; timeframe: string }>
): LocalSnapshotEntry[] {
  return entries.map((entry) => ({
    id: generateUUID(),
    instrumentId: entry.instrumentId,
    timeframe: entry.timeframe,
  }));
}

/**
 * Convert local entries to payload for persistence (strip local id)
 */
function toPayload(
  entries: LocalSnapshotEntry[]
): Array<{ instrumentId: string; timeframe: string }> {
  return entries.map(({ instrumentId, timeframe }) => ({
    instrumentId,
    timeframe,
  }));
}

/**
 * Serialize payload for dirty comparison
 */
function serializePayload(
  entries: Array<{ instrumentId: string; timeframe: string }>
): string {
  return JSON.stringify(entries);
}

// ============================================================================
// Component
// ============================================================================

export const MarketSnapshotWorkflowConfigModal: React.FC<
  MarketSnapshotWorkflowConfigModalProps
> = ({ isOpen, onClose, onSaveSuccess }) => {
  const navigate = useNavigate();
  const { showToast } = useToastNotification();

  // Local editing state
  const [localEntries, setLocalEntries] = useState<LocalSnapshotEntry[]>([]);
  const [visibleInstruments, setVisibleInstruments] = useState<Instrument[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, EntryValidationErrors>
  >({});

  // Refresh key for sibling InstrumentDropdown instances
  const [instrumentRefreshKey, setInstrumentRefreshKey] = useState(0);

  // Baseline ref for dirty tracking (stores serialized payload, not local entries)
  const baselinePayloadRef = useRef<string>('[]');

  // Track previous isOpen to detect open transition
  const wasOpenRef = useRef(false);

  // Portal container ref for TimeframeDropdown popover (fixes aria-hidden warning)
  const portalContainerRef = useRef<HTMLDivElement>(null);

  // Compute dirty state by comparing current payload to baseline
  const currentPayload = useMemo(() => toPayload(localEntries), [localEntries]);
  const isDirty = serializePayload(currentPayload) !== baselinePayloadRef.current;

  // Check if save should be disabled
  const hasBlockingErrors = useMemo(() => {
    // Check for any validation errors
    const hasErrors = Object.values(validationErrors).some(
      (errors) => Object.keys(errors).length > 0
    );
    if (hasErrors) return true;

    // Check for incomplete entries (missing timeframe)
    const hasIncomplete = localEntries.some(
      (entry) => !entry.timeframe || entry.timeframe.trim() === ''
    );
    return hasIncomplete;
  }, [validationErrors, localEntries]);

  // ============================================================================
  // Validation
  // ============================================================================

  const validateAllEntries = useCallback((entries: LocalSnapshotEntry[]) => {
    const errors: Record<string, EntryValidationErrors> = {};

    // Track seen combinations for duplicate detection
    const seenCombinations = new Map<string, string>(); // key -> first entry id

    for (const entry of entries) {
      const entryErrors: EntryValidationErrors = {};

      // Check required instrumentId
      if (!entry.instrumentId || entry.instrumentId.trim() === '') {
        entryErrors.instrumentId = 'Instrument is required';
      }

      // Check required timeframe
      if (!entry.timeframe || entry.timeframe.trim() === '') {
        entryErrors.timeframe = 'Timeframe is required';
      }

      // Check for duplicates (only if both fields are present)
      if (entry.instrumentId && entry.timeframe) {
        const key = `${entry.instrumentId}:${entry.timeframe}`;
        const existingId = seenCombinations.get(key);
        if (existingId) {
          // Mark both the original and duplicate
          entryErrors.duplicate = 'Duplicate instrument + timeframe combination';
          // Also mark the original if not already marked
          if (!errors[existingId]?.duplicate) {
            errors[existingId] = {
              ...errors[existingId],
              duplicate: 'Duplicate instrument + timeframe combination',
            };
          }
        } else {
          seenCombinations.set(key, entry.id);
        }
      }

      if (Object.keys(entryErrors).length > 0) {
        errors[entry.id] = entryErrors;
      }
    }

    setValidationErrors(errors);
    return errors;
  }, []);

  // ============================================================================
  // Initialization
  // ============================================================================

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      // Modal just opened - load data
      const loadData = async () => {
        setIsLoading(true);
        try {
          // Load instruments and settings in parallel
          const [allInstruments, settings] = await Promise.all([
            instrumentService.getAll(),
            marketSnapshotSettingsService.getOrCreateDefaultSettings(),
          ]);

          // Filter to visible instruments (active + inactive, excluding archived)
          const visible = allInstruments.filter((i) => i.status !== 'archived');
          setVisibleInstruments(visible);

          // Convert persisted entries to local entries (fallback to [] for safety)
          const local = toLocalEntries(settings.defaultSnapshots ?? []);
          setLocalEntries(local);

          // Set baseline for dirty tracking
          baselinePayloadRef.current = serializePayload(settings.defaultSnapshots ?? []);

          // Validate loaded entries (catch persisted duplicates or missing data)
          validateAllEntries(local);
        } catch (err) {
          console.error('Failed to load market snapshot settings:', err);
          // Only show toast if modal is still open (avoid noisy errors on background loads)
          if (isOpen) {
            showToast({
              title: 'Load failed',
              description: 'Could not load settings. Please try again.',
              color: 'danger',
            });
          }
        } finally {
          setIsLoading(false);
        }
      };
      loadData();
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, showToast, validateAllEntries]);

  // ============================================================================
  // CRUD Handlers
  // ============================================================================

  const handleAddEntry = useCallback(() => {
    const newEntry: LocalSnapshotEntry = {
      id: generateUUID(),
      instrumentId: '', // User must select via InstrumentDropdown
      timeframe: '', // User must select
    };

    setLocalEntries((prev) => {
      const updated = [...prev, newEntry];
      validateAllEntries(updated);
      return updated;
    });
  }, [validateAllEntries]);

  const handleTimeframeChange = useCallback(
    (entryId: string, timeframe: string) => {
      setLocalEntries((prev) => {
        const updated = prev.map((entry) =>
          entry.id === entryId ? { ...entry, timeframe } : entry
        );
        validateAllEntries(updated);
        return updated;
      });
    },
    [validateAllEntries]
  );

  const handleDeleteEntry = useCallback(
    (entryId: string) => {
      setLocalEntries((prev) => {
        const updated = prev.filter((entry) => entry.id !== entryId);
        // Clear validation errors for deleted entry
        setValidationErrors((prevErrors) => {
          const newErrors = { ...prevErrors };
          delete newErrors[entryId];
          return newErrors;
        });
        // Re-validate to clear any duplicate errors
        validateAllEntries(updated);
        return updated;
      });
    },
    [validateAllEntries]
  );

  const handleReorder = useCallback(
    (reorderedEntries: LocalSnapshotEntry[]) => {
      setLocalEntries(reorderedEntries);
      validateAllEntries(reorderedEntries);
    },
    [validateAllEntries]
  );

  const handleInstrumentChange = useCallback(
    (entryId: string, instrumentId: string) => {
      setLocalEntries((prev) => {
        const updated = prev.map((entry) =>
          entry.id === entryId ? { ...entry, instrumentId } : entry
        );
        validateAllEntries(updated);
        return updated;
      });
    },
    [validateAllEntries]
  );

  /**
   * Refresh visible instruments list (called after reactivation in InstrumentDropdown)
   */
  const refreshInstruments = useCallback(async () => {
    try {
      const allInstruments = await instrumentService.getAll();
      const visible = allInstruments.filter((i) => i.status !== 'archived');
      setVisibleInstruments(visible);
      // Increment refresh key to trigger sibling InstrumentDropdown reloads
      setInstrumentRefreshKey((k) => k + 1);
    } catch (err) {
      console.error('Failed to refresh instruments:', err);
    }
  }, []);

  // ============================================================================
  // Save/Discard Handlers
  // ============================================================================

  const handleSave = useCallback(async () => {
    // Validate all entries
    const errors = validateAllEntries(localEntries);
    if (Object.keys(errors).length > 0) {
      showToast({
        title: 'Validation errors',
        description: 'Please fix the errors before saving.',
        color: 'warning',
      });
      return;
    }

    setIsSaving(true);

    try {
      const payload = toPayload(localEntries);
      await marketSnapshotSettingsService.updateDefaultSnapshots(payload);

      // Update baseline
      baselinePayloadRef.current = serializePayload(payload);

      showToast({
        title: 'Settings saved',
        description: 'Default snapshots updated successfully.',
        color: 'success',
      });

      onSaveSuccess();
      onClose();
    } catch (err) {
      console.error('Save failed:', err);
      showToast({
        title: 'Save failed',
        description: 'An error occurred while saving. Please try again.',
        color: 'danger',
      });
    } finally {
      setIsSaving(false);
    }
  }, [localEntries, validateAllEntries, showToast, onSaveSuccess, onClose]);

  const handleDiscard = useCallback(() => {
    // Reset to baseline
    const baselineEntries = JSON.parse(baselinePayloadRef.current) as Array<{
      instrumentId: string;
      timeframe: string;
    }>;
    setLocalEntries(toLocalEntries(baselineEntries));
    setValidationErrors({});
    onClose();
  }, [onClose]);

  // ============================================================================
  // Navigation
  // ============================================================================

  const handleNavigateToFieldBuilder = useCallback(() => {
    onClose();
    navigate('/settings/workflow/market-snapshots');
  }, [onClose, navigate]);

  const handleNavigateToInstruments = useCallback(() => {
    onClose();
    navigate('/settings/portfolio/reference-data?tab=instruments');
  }, [onClose, navigate]);

  // ============================================================================
  // Row Renderer
  // ============================================================================

  const renderEntryRow = useCallback(
    (entry: LocalSnapshotEntry, dragHandleProps: DragHandleProps) => {
      const errors = validationErrors[entry.id] || {};

      return (
        <Card
          shadow="sm"
          radius="sm"
          className={dragHandleProps.isDragging ? 'opacity-50 shadow-lg' : ''}
        >
          <CardBody className="p-3">
            {/* Mobile Layout */}
            <div className="md:hidden flex items-start gap-3">
              <DragHandle dragHandleProps={dragHandleProps} />

              <div className="flex-1 space-y-2">
                {/* Instrument dropdown */}
                <InstrumentDropdown
                  value={entry.instrumentId || null}
                  onChange={(id) => handleInstrumentChange(entry.id, id)}
                  placeholder="Select instrument"
                  size="sm"
                  disableAnimation
                  {...(portalContainerRef.current && { popoverProps: { portalContainer: portalContainerRef.current } })}
                  onInstrumentsChanged={refreshInstruments}
                  refreshKey={instrumentRefreshKey}
                />

                {/* Timeframe dropdown */}
                <TimeframeDropdown
                  value={entry.timeframe || null}
                  onChange={(value) => handleTimeframeChange(entry.id, value)}
                  placeholder="Select timeframe"
                  size="sm"
                  disableAnimation
                  {...(portalContainerRef.current && { popoverProps: { portalContainer: portalContainerRef.current } })}
                />

                {/* Errors */}
                {errors.instrumentId && (
                  <p className="text-danger text-xs">{errors.instrumentId}</p>
                )}
                {errors.timeframe && (
                  <p className="text-danger text-xs">{errors.timeframe}</p>
                )}
                {errors.duplicate && (
                  <p className="text-danger text-xs">{errors.duplicate}</p>
                )}
              </div>

              <Button
                isIconOnly
                size="sm"
                variant="light"
                color="danger"
                onPress={() => handleDeleteEntry(entry.id)}
                aria-label="Delete entry"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>

            {/* Desktop Layout */}
            <div className="hidden md:flex items-center gap-3">
              <DragHandle dragHandleProps={dragHandleProps} />

              {/* Instrument dropdown */}
              <div className="flex-1 min-w-0">
                <InstrumentDropdown
                  value={entry.instrumentId || null}
                  onChange={(id) => handleInstrumentChange(entry.id, id)}
                  placeholder="Select instrument"
                  size="sm"
                  disableAnimation
                  {...(portalContainerRef.current && { popoverProps: { portalContainer: portalContainerRef.current } })}
                  onInstrumentsChanged={refreshInstruments}
                  refreshKey={instrumentRefreshKey}
                />
                {errors.instrumentId && (
                  <p className="text-danger text-xs mt-1">{errors.instrumentId}</p>
                )}
                {errors.duplicate && (
                  <p className="text-danger text-xs mt-1">{errors.duplicate}</p>
                )}
              </div>

              {/* Timeframe dropdown */}
              <div className="w-48">
                <TimeframeDropdown
                  value={entry.timeframe || null}
                  onChange={(value) => handleTimeframeChange(entry.id, value)}
                  placeholder="Select timeframe"
                  size="sm"
                  disableAnimation
                  {...(portalContainerRef.current && { popoverProps: { portalContainer: portalContainerRef.current } })}
                />
                {errors.timeframe && (
                  <p className="text-danger text-xs mt-1">{errors.timeframe}</p>
                )}
              </div>

              {/* Delete button */}
              <Button
                isIconOnly
                size="sm"
                variant="light"
                color="danger"
                onPress={() => handleDeleteEntry(entry.id)}
                aria-label="Delete entry"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </CardBody>
        </Card>
      );
    },
    [validationErrors, handleInstrumentChange, handleTimeframeChange, handleDeleteEntry, refreshInstruments, instrumentRefreshKey]
  );

  // ============================================================================
  // Render
  // ============================================================================

  // Only block Add Entry when there are NO visible instruments (active or inactive)
  const hasNoInstruments = visibleInstruments.length === 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      placement="top"
      scrollBehavior="inside"
      size="2xl"
      isDismissable={!isDirty}
      hideCloseButton={isDirty}
      isKeyboardDismissDisabled={isDirty}
    >
      <ModalContent>
        <ModalHeader>
          <span className="text-lg font-semibold">Market Snapshot Settings</span>
        </ModalHeader>
        <ModalBody className="gap-4">
          <div ref={portalContainerRef}>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <span className="text-default-500">Loading...</span>
            </div>
          ) : (
            <>
              {/* No instruments alert */}
              {hasNoInstruments && (
                <Alert
                  color="warning"
                  title="No instruments configured"
                  description="Add instruments to your library before configuring default snapshots."
                  endContent={
                    <Button
                      size="sm"
                      variant="flat"
                      color="warning"
                      onPress={handleNavigateToInstruments}
                    >
                      Configure Instruments
                    </Button>
                  }
                />
              )}

              {/* Entries list */}
              {localEntries.length > 0 ? (
                <div className="space-y-2">
                  {/* Column headers (desktop only) */}
                  <div className="hidden md:flex items-center gap-3 px-3 text-sm text-default-500 font-medium">
                    <div className="w-6" />
                    <div className="flex-1">Instrument</div>
                    <div className="w-48">Timeframe</div>
                    <div className="w-8" />
                  </div>
                  <SortableList
                    items={localEntries}
                    onReorder={handleReorder}
                    renderItem={renderEntryRow}
                    className="space-y-2"
                  />
                </div>
              ) : (
                <EmptyState
                  title="No default snapshots configured"
                  description="Add instrument/timeframe pairs to auto-populate when starting a new trading session."
                />
              )}

              {/* Add Entry button */}
              <Button
                variant="flat"
                color="primary"
                startContent={<Plus className="w-4 h-4" />}
                onPress={handleAddEntry}
                isDisabled={hasNoInstruments}
                className="w-full md:w-auto"
              >
                Add Entry
              </Button>

              {/* Helper text when no instruments */}
              {hasNoInstruments && localEntries.length === 0 && (
                <p className="text-sm text-default-500">
                  Add instruments in Settings first to configure default snapshots.
                </p>
              )}

              {/* Divider */}
              <div className="border-t border-default-200 my-2" />

              {/* Field Builder link */}
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1">
                  <p className="text-sm font-medium">Snapshot Fields</p>
                  <p className="text-sm text-default-500">
                    Configure which fields to capture for each snapshot
                  </p>
                </div>
                <Link
                  onPress={handleNavigateToFieldBuilder}
                  showAnchorIcon
                  size="sm"
                  className="cursor-pointer"
                >
                  Configure Snapshot Fields
                </Link>
              </div>
            </>
          )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="light"
            onPress={isDirty ? handleDiscard : onClose}
            isDisabled={isSaving}
          >
            {isDirty ? 'Discard' : 'Cancel'}
          </Button>
          <Button
            color="primary"
            onPress={handleSave}
            isDisabled={!isDirty || isSaving || hasBlockingErrors}
            isLoading={isSaving}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default MarketSnapshotWorkflowConfigModal;
